import React, { useRef } from "react";
import { toPng } from "html-to-image";
import { Share2, Download } from "lucide-react";
import { notify } from "../lib/notify";

/**
 * ShareCard — wraps a result block in a styled card and offers a "Download PNG" button.
 * Pass `title`, `subtitle`, `score`, `band`, `color`. Optional `extras` for breakdown lines.
 */
export default function ShareCard({ title, subtitle, score, band, color = "amber", extras = [], onSave }) {
  const ref = useRef(null);

  const download = async () => {
    if (!ref.current) return;
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `riskpedia-sg-${(title || "result").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      a.click();
      notify.success("Card downloaded — share it on LinkedIn.");
    } catch {
      notify.error("Could not export image.");
    }
  };

  const colorHex = { emerald: "#10B981", amber: "#F59E0B", rose: "#EF4444" }[color] || "#F59E0B";

  return (
    <div data-testid="share-card-wrapper">
      <div ref={ref} className="rp-card p-8" style={{ width: 560, maxWidth: "100%" }} data-testid="share-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-display text-xl font-bold" style={{ color: "var(--rp-primary)" }}>RiskPedia</div>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-primary)", color: "#fff" }}>SG</span>
          </div>
          <span className="text-[10px] font-mono text-slate-400">riskpedia.sg</span>
        </div>
        <div className="mt-8">
          <div className="overline">{title}</div>
          {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
          <div className="flex items-baseline gap-3 mt-5">
            <div className="font-display text-7xl font-bold tracking-tight" style={{ color: colorHex }}>{score}</div>
            <div className="font-mono text-sm text-slate-400">/100</div>
          </div>
          <div className="font-display text-2xl font-semibold mt-3" style={{ color: colorHex }}>{band}</div>
        </div>
        {extras.length > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="overline-muted mb-3">Breakdown</div>
            <div className="space-y-1.5 text-xs">
              {extras.slice(0, 6).map((e, i) => (
                <div key={i} className="flex justify-between"><span>{e.label}</span><span className="font-mono">{e.value}</span></div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-between text-[10px] font-mono text-slate-400">
          <span>Educational only · Not legal/regulatory advice</span>
          <span>{new Date().toLocaleDateString("en-SG")}</span>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={download} className="btn-accent" data-testid="share-download"><Download size={14} /> Download PNG</button>
        {onSave && <button onClick={onSave} className="btn-outline" data-testid="share-save"><Share2 size={14} /> Save to my account</button>}
      </div>
    </div>
  );
}
